import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Row, Col, OverlayTrigger, Tooltip } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { useMutation } from "@apollo/client";
import { faInfoCircle } from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Select from "react-select";
import { UPDATE_GROUP_VERSION_MUTATION } from "../../../../graphql/mutations";

const EditGroupVersionModal = ({ show, groupVersion, handleClose, env, tenantName, tenantId, handleEnvChange, viewOnly }) => {
  const navigate = useNavigate();
  const [formErrors, setFormErrors] = useState({});

  const [formData, setFormData] = useState({
    name: "",
    operator: "",
    segment: "",
    assetType: "",
    groupId: "",
    ogmpYear: "",
    ogmpLevel: "",
    plannedSiteSurveys: "",
    productionCh4: "",
    productionCh4Unit: "kgch4", // default
    typeOfEquity: "",
    equityFraction: "",
    uncertaintyFraction: "",
    location: "",
    comments: ""
  });

  const ogmpLevelOptions = [
    { value: "L3", label: "L3" },
    { value: "L3/L4", label: "L3/L4" },
    { value: "L3/L4/L5", label: "L3/L4/L5" },
    { value: "L4", label: "L4" },
    { value: "L4/L5", label: "L4/L5" },
    { value: "L5", label: "L5" }
  ];

  const unitOptions = [
    { value: "kgch4", label: "kgCH4" },
    { value: "tch4", label: "tCH4" }
  ];

  useEffect(() => {
    if (groupVersion) {
      setFormData({
        name: groupVersion.name || "",
        operator: groupVersion.operator || "",
        segment: groupVersion.segment || "",
        assetType: groupVersion.assetType || "",
        groupId: groupVersion.groupId || "",
        ogmpYear: groupVersion.ogmpYear || "",
        ogmpLevel: groupVersion.ogmpLevel || "",
        plannedSiteSurveys: groupVersion.plannedSiteSurveys || "",
        productionCh4: groupVersion.productionCh4 || "",
        productionCh4Unit: groupVersion.productionCh4Unit || "kgch4",
        typeOfEquity: groupVersion.typeOfEquity || "",
        equityFraction: groupVersion.equityFraction || "",
        uncertaintyFraction: groupVersion.uncertaintyFraction || "",
        location: groupVersion.location || "",
        comments: groupVersion.comments || ""
      });

      setFormErrors({});
    }
  }, [groupVersion]);

  const handleInputChange = (e) => {
    const { id, value } = e.target;
    setFormData((prevData) => ({ ...prevData, [id]: value }));

    if (formErrors[id]) {
      setFormErrors((prev) => ({ ...prev, [id]: null }));
    }
  };

  const handleSelectChange = (id, selectedOption) => {
    const value = selectedOption ? selectedOption.value : "";
    setFormData((prevData) => ({
      ...prevData,
      [id]: value
    }));

    if (formErrors[id]) {
      setFormErrors((prev) => ({ ...prev, [id]: null }));
    }
  };

  const [updateGroupVersion, { loading: mutationLoading }] = useMutation(UPDATE_GROUP_VERSION_MUTATION);

  const validateForm = () => {
    const errors = {};
    const requiredFields = ["ogmpYear", "ogmpLevel", "productionCh4", "productionCh4Unit", "uncertaintyFraction"];

    requiredFields.forEach((field) => {
      if (!formData[field]) {
        errors[field] = "This field is required";
      }
    });

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error("Please fill in all required fields");
      return;
    }

    try {
      const plannedSiteSurveys = formData.plannedSiteSurveys ? parseInt(formData.plannedSiteSurveys, 10) : null;
      const productionCh4 = formData.productionCh4 ? parseFloat(formData.productionCh4) : null;
      const productionCh4Unit = formData.productionCh4 ? formData.productionCh4Unit : null;
      const equityFraction = formData.equityFraction ? parseFloat(formData.equityFraction) : null;
      const uncertaintyFraction = formData.uncertaintyFraction ? parseFloat(formData.uncertaintyFraction) : null;

      const { data } = await updateGroupVersion({
        variables: {
          id: groupVersion.id,
          groupVersionInput: {
            comments: formData?.comments,
            equityFraction: equityFraction,
            groupId: groupVersion?.groupId,
            ogmpLevel: formData?.ogmpLevel,
            plannedSiteSurveys: plannedSiteSurveys,
            productionCh4: productionCh4,
            productionCh4Unit: productionCh4Unit,
            typeOfEquity: formData?.typeOfEquity,
            uncertaintyFraction: uncertaintyFraction
          }
        }
      });

      if (data?.updateGroupVersion) {
        handleClose();
        setTimeout(() => {
          toast.success("Group version updated successfully.");
          navigate("/group-versions");
          handleEnvChange(env, tenantId, tenantName);
        }, 200);
      }
    } catch (error) {
      console.error("Update error:", error);
      toast.error("Failed to update group version.");
    }
  };

  return (
    <Modal show={show} onHide={handleClose} centered size="lg">
      <Modal.Header closeButton className="d-flex justify-content-center">
        <Modal.Title className="w-100 text-center">{viewOnly ? "View Group Version" : "Edit Group Version"}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="project-group-section mb-4 p-3 bg-light border rounded">
          <Form onSubmit={handleSubmit}>
            {/* Group Information (Read-only) */}
            <Row>
              <Col md={6}>
                <Form.Group controlId="name" className="mb-4">
                  <Form.Label>Group Name</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Group Name (readonly)"
                    value={formData.name}
                    disabled
                    readOnly
                    className="bg-light"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group controlId="location" className="mb-4">
                  <Form.Label>Group Location</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Location (readonly)"
                    value={formData.location}
                    disabled
                    readOnly
                    className="bg-light"
                  />
                </Form.Group>
              </Col>
            </Row>

            {/* Group Details (Read-only) */}
            <Row>
              <Col md={6}>
                <Form.Group controlId="segment" className="mb-4">
                  <Form.Label>Group Segment</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Segment (readonly)"
                    value={formData.segment}
                    disabled
                    readOnly
                    className="bg-light"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group controlId="assetType" className="mb-4">
                  <Form.Label>Group Asset Type</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Asset Type (readonly)"
                    value={formData.assetType}
                    disabled
                    readOnly
                    className="bg-light"
                  />
                </Form.Group>
              </Col>
            </Row>

            {/* OGMP Info */}
            <Row>
              <Col md={6}>
                <Form.Group controlId="ogmpYear" className="mb-4">
                  <Form.Label>
                    <strong>OGMP Year</strong>
                    <OverlayTrigger
                      placement="right"
                      overlay={
                        <Tooltip id="info-tooltip" className="text-start">
                          The ogmp year is unique for each project group. To change it, delete and create a new version.
                        </Tooltip>
                      }
                    >
                      <span style={{ cursor: "pointer", marginLeft: "5px" }}>
                        <FontAwesomeIcon icon={faInfoCircle} className="text-primary" style={{ fontSize: "16px" }} />
                      </span>
                    </OverlayTrigger>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="OGMP Year (readonly)"
                    value={formData.ogmpYear}
                    disabled
                    readOnly
                    className="bg-light"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group controlId="ogmpLevel" className="mb-4">
                  <Form.Label>
                    <strong>OGMP Level</strong>
                    <span className="text-danger">*</span>
                  </Form.Label>
                  <div>
                    <Select
                      options={ogmpLevelOptions}
                      onChange={(option) => handleSelectChange("ogmpLevel", option)}
                      value={ogmpLevelOptions.find((option) => option.value === formData.ogmpLevel) || null}
                      placeholder="Select OGMP Level"
                      isDisabled={viewOnly}
                      isClearable
                      isSearchable
                      className={formErrors?.ogmpLevel ? "is-invalid" : ""}
                    />
                    {formErrors?.ogmpLevel && <div className="invalid-feedback d-block">{formErrors.ogmpLevel}</div>}
                  </div>
                </Form.Group>
              </Col>
            </Row>

            {/* Site Surveys and Uncertainty */}
            <Row>
              <Col md={6}>
                <Form.Group controlId="plannedSiteSurveys" className="mb-4">
                  <Form.Label>
                    <strong>Planned Site Surveys</strong>
                  </Form.Label>
                  <Form.Control
                    type="number"
                    placeholder="Enter Planned Number of Site Surveys"
                    value={formData.plannedSiteSurveys}
                    onChange={handleInputChange}
                    disabled={viewOnly}
                    min="0"
                    max="5000"
                    step="1"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group controlId="uncertaintyFraction" className="mb-4">
                  <Form.Label>
                    <strong>Uncertainty Fraction</strong>
                    <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="number"
                    placeholder="Enter Uncertainty Fraction (e.g. 0.1 for 10%)"
                    value={formData.uncertaintyFraction}
                    onChange={handleInputChange}
                    disabled={viewOnly}
                    min="0"
                    max="1"
                    step="0.01"
                  />
                  {formErrors?.uncertaintyFraction && <div className="invalid-feedback d-block">{formErrors.uncertaintyFraction}</div>}
                </Form.Group>
              </Col>
            </Row>

            {/* Production CH4 and Unit */}
            <Row>
              <Col md={6}>
                <Form.Group controlId="productionCh4" className="mb-4">
                  <Form.Label>
                    <strong>Production CH4</strong>
                    <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="number"
                    placeholder="Enter Production CH4 (for the OGMP year)"
                    value={formData.productionCh4}
                    onChange={handleInputChange}
                    disabled={viewOnly}
                    min="0"
                    max="50000000"
                    step="1000"
                  />
                  {formErrors?.productionCh4 && <div className="invalid-feedback d-block">{formErrors.productionCh4}</div>}
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group controlId="productionCh4Unit" className="mb-4">
                  <Form.Label>
                    <strong>Production CH4 Unit</strong>
                    <span className="text-danger">*</span>
                  </Form.Label>
                  <div>
                    <Select
                      options={unitOptions}
                      onChange={(option) => handleSelectChange("productionCh4Unit", option)}
                      value={unitOptions.find((option) => option.value === formData.productionCh4Unit) || null}
                      placeholder="Select Unit"
                      isDisabled={viewOnly}
                      isClearable
                      isSearchable
                    />
                    {formErrors?.productionCh4Unit && <div className="invalid-feedback d-block">{formErrors.productionCh4Unit}</div>}
                  </div>
                </Form.Group>
              </Col>
            </Row>

            {/* Equity Information */}
            <Row>
              <Col md={6}>
                <Form.Group controlId="typeOfEquity" className="mb-4">
                  <Form.Label>
                    <strong>Type of Equity</strong>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter Type of Equity (e.g. Common Stock)"
                    value={formData.typeOfEquity}
                    onChange={handleInputChange}
                    disabled={viewOnly}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group controlId="equityFraction" className="mb-4">
                  <Form.Label>
                    <strong>Equity Fraction</strong>
                  </Form.Label>
                  <Form.Control
                    type="number"
                    placeholder="Enter Equity Fraction (e.g. 0.25 for 25%)"
                    value={formData.equityFraction}
                    onChange={handleInputChange}
                    disabled={viewOnly}
                    min="0"
                    max="1"
                    step="0.01"
                  />
                </Form.Group>
              </Col>
            </Row>

            {/* Comments */}
            <Row>
              <Col md={12}>
                <Form.Group controlId="comments" className="mb-4">
                  <Form.Label>
                    <strong>Comments</strong>
                  </Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={6}
                    placeholder="Enter any additional comments"
                    value={formData.comments}
                    onChange={handleInputChange}
                    disabled={viewOnly}
                  />
                </Form.Group>
              </Col>
            </Row>

            {!viewOnly && (
              <div className="d-grid mt-4">
                <Button variant="primary" type="submit" style={{ width: "50%" }} className="mx-auto" disabled={mutationLoading}>
                  {mutationLoading ? "Updating Group Version..." : "Update Group Version"}
                </Button>
              </div>
            )}
          </Form>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export { EditGroupVersionModal };
